import { useLayoutEffect, useRef, type InputHTMLAttributes } from 'react';

/** "5000.5" -> "5,000.5": thousands separators on a raw typed number, keeping a trailing "." or partial decimals as typed. */
export function groupDigits(raw: string): string {
  const [i = '', ...rest] = raw.split('.');
  const grouped = i.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  return rest.length ? `${grouped}.${rest.join('.')}` : grouped;
}

type Props = Omit<InputHTMLAttributes<HTMLInputElement>, 'value' | 'onChange'> & {
  /** Raw value without separators ("5000.5"). */
  value: string;
  /** Called with the typed text, separators stripped. Validate here; ignore to reject the keystroke. */
  onValue: (raw: string) => void;
};

/** Numeric text input that shows thousands separators (5,000) while state stays a plain decimal string. */
export function NumInput({ value, onValue, inputMode = 'decimal', ...rest }: Props) {
  const ref = useRef<HTMLInputElement>(null);
  // Characters (other than separators) before the caret, so it can be restored after re-grouping.
  const caret = useRef<number | null>(null);

  useLayoutEffect(() => {
    const el = ref.current;
    const n = caret.current;
    caret.current = null;
    if (!el || n === null || document.activeElement !== el) return;
    let pos = 0;
    for (let seen = 0; pos < el.value.length && seen < n; pos++) if (el.value[pos] !== ',') seen++;
    el.setSelectionRange(pos, pos);
  }, [value]);

  return (
    <input
      {...rest}
      ref={ref}
      inputMode={inputMode}
      value={groupDigits(value)}
      onChange={(e) => {
        const t = e.target;
        caret.current = t.selectionStart === null ? null : t.value.slice(0, t.selectionStart).replace(/,/g, '').length;
        onValue(t.value.replace(/,/g, '').trim());
      }}
    />
  );
}
